/**
 * MyEstimates (/account)
 *
 * The user's own saved estimates plus claimable guest estimates. Each estimate
 * offers View, "New estimate from this template", and Delete (soft, confirm
 * modal). Responsive: a table on >= md, stacked cards on phones so the row
 * actions are never pushed off-screen.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  FileText,
  CheckCircle2,
  Plus,
  Eye,
  MessageSquare,
  Copy,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEstimator } from '../context/EstimatorContext';
import {
  fetchClaimableEstimates,
  claimEstimates,
  fetchMyEstimates,
  deleteMyEstimate,
  loadEstimateByGuid,
  ClaimableEstimate,
  MyEstimate,
} from '../services/api';
import { buildTemplateLoadPayload } from '../utils/templatePayload';
import { calculationModeLabel, spaceTypeLabel } from '../components/results/configDataPoints';
import { markSplashSeen } from '../utils/splashSeen';
import type { WizardState, Currency } from '../types';

// "FullRoom" → "Full Room", "living-space" → "Living Space"; null-safe.
// Only a fallback now: known modes and space types go through the shared
// translated labels, and this covers a value the app no longer offers (an old
// estimate saved under a retired space type) so it still reads as words.
const prettify = (value: string | null): string | null => {
  if (!value) return null;
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const subtitleOf = (est: MyEstimate): string => {
  const mode = est.calculationMode ? calculationModeLabel(est.calculationMode) : null;
  const space = est.spaceType ? spaceTypeLabel(est.spaceType, prettify(est.spaceType) ?? undefined) : null;
  return [mode, space].filter(Boolean).join(' | ');
};

const locationOf = (est: MyEstimate): string =>
  est.city && est.region ? `${est.city}, ${est.region}` : est.city || est.region || '—';

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr.replace(' ', 'T'));
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
};

export const MyEstimates: React.FC = () => {
  const { t } = useTranslation('account');
  const { user } = useAuth();
  const { loadState } = useEstimator();
  const navigate = useNavigate();

  const [claimable, setClaimable] = useState<ClaimableEstimate[]>([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimedCount, setClaimedCount] = useState<number | null>(null);

  const [mine, setMine] = useState<MyEstimate[]>([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<MyEstimate | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [templateBusy, setTemplateBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setClaimable(await fetchClaimableEstimates());
    } catch {
      setClaimable([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMine = useCallback(async () => {
    setLoadingMine(true);
    try {
      setMine(await fetchMyEstimates());
    } catch {
      setMine([]);
    } finally {
      setLoadingMine(false);
    }
  }, []);

  useEffect(() => { load(); loadMine(); }, [load, loadMine]);

  const handleClaimAll = async () => {
    setClaiming(true);
    try {
      const n = await claimEstimates();
      setClaimedCount(n);
      await load();
      await loadMine(); // claimed estimates are now owned
    } catch {
      setClaimedCount(null);
    } finally {
      setClaiming(false);
    }
  };

  // "New estimate from this template": fetch the full estimate, clone its
  // config (minus personal info), and drop the user into the wizard.
  const handleNewFromTemplate = async (guid: string) => {
    setTemplateBusy(guid);
    setActionError(null);
    try {
      const data = await loadEstimateByGuid(guid);
      if (!data.wizardState) {
        setActionError(t('estimates.errors.noConfig'));
        return;
      }
      loadState(buildTemplateLoadPayload(data.wizardState as WizardState & { currency?: Currency }));
      markSplashSeen(null);
      navigate('/');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setActionError(t('estimates.errors.template'));
    } finally {
      setTemplateBusy(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteMyEstimate(deleteTarget.guid);
      setMine(prev => prev.filter(e => e.guid !== deleteTarget.guid));
      setDeleteTarget(null);
    } catch {
      setActionError(t('estimates.errors.delete'));
    } finally {
      setDeleting(false);
    }
  };

  // Shared action buttons. On mobile (`labeled`) the three share one line as
  // equal-width buttons with short text; the desktop table shows icon-only
  // buttons with tooltips.
  const renderActions = (est: MyEstimate, labeled: boolean) => (
    <div className={`d-flex gap-2 ${labeled ? '' : 'justify-content-end'}`}>
      <button
        type="button"
        className={`btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 ${labeled ? 'flex-fill' : 'py-0 px-1'}`}
        title={t('estimates.actions.viewTitle')}
        onClick={() => navigate(`/estimate/${est.guid}`)}
      >
        <Eye size={15} />{labeled && <span>{t('estimates.actions.view')}</span>}
      </button>
      <button
        type="button"
        className={`btn btn-sm btn-outline-secondary d-flex align-items-center justify-content-center gap-1 ${labeled ? 'flex-fill' : 'py-0 px-1'}`}
        title={t('estimates.actions.copyTitle')}
        disabled={templateBusy === est.guid}
        onClick={() => handleNewFromTemplate(est.guid)}
      >
        {templateBusy === est.guid
          ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
          : <Copy size={15} />}
        {labeled && <span>{t('estimates.actions.copy')}</span>}
      </button>
      <button
        type="button"
        className={`btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center gap-1 ${labeled ? 'flex-fill' : 'py-0 px-1'}`}
        title={t('estimates.actions.deleteTitle')}
        onClick={() => setDeleteTarget(est)}
      >
        <Trash2 size={15} />{labeled && <span>{t('estimates.actions.delete')}</span>}
      </button>
    </div>
  );

  return (
    <>
      {user && !user.emailVerified && (
        <div className="alert alert-warning small">{t('estimates.unverified')}</div>
      )}

      {claimedCount !== null && (
        <div className="alert alert-success small d-flex align-items-center gap-2">
          <CheckCircle2 size={16} /> {t('estimates.claimed', { count: claimedCount })}
        </div>
      )}

      {actionError && (
        <div className="alert alert-danger small d-flex align-items-center justify-content-between">
          <span>{actionError}</span>
          <button type="button" className="btn-close" aria-label={t('estimates.dismiss')} onClick={() => setActionError(null)} />
        </div>
      )}

      {/* My estimates */}
      <div className="card shadow-sm mb-4 px-2 px-sm-0">
        <div className="card-body px-0 px-sm-3 py-3 py-sm-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold m-0">{t('tabs.estimates')}</h5>
            <button className="btn btn-primary btn-sm d-inline-flex align-items-center gap-2" onClick={() => navigate('/')}>
              <Plus size={16} /> {t('estimates.new')}
            </button>
          </div>

          {loadingMine ? (
            <div className="text-center py-4">
              <Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : mine.length === 0 ? (
            <p className="text-muted small mb-0">{t('estimates.empty')}</p>
          ) : (
            <>
              {/* Desktop / tablet: table */}
              <div className="d-none d-md-block table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr className="text-muted small">
                      <th>{t('estimates.columns.project')}</th>
                      <th>{t('estimates.columns.location')}</th>
                      <th>{t('estimates.columns.created')}</th>
                      <th className="text-center" title={t('estimates.columns.views')}><Eye size={14} /></th>
                      <th className="text-center" title={t('results:comments.title')}><MessageSquare size={14} /></th>
                      <th className="text-end">{t('estimates.columns.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mine.map(est => {
                      const subtitle = subtitleOf(est);
                      return (
                        <tr key={est.guid}>
                          <td>
                            <div className="fw-semibold small">{est.estimateName || <span className="text-muted fst-italic">{t('estimates.untitled')}</span>}</div>
                            {subtitle && <div className="text-muted" style={{ fontSize: 12 }}>{subtitle}</div>}
                          </td>
                          <td className="small">{locationOf(est)}</td>
                          <td className="small text-nowrap">{formatDate(est.createdAt)}</td>
                          <td className="text-center small">
                            <span className={est.views > 0 ? 'fw-bold text-dark' : 'text-muted'}>{est.views}</span>
                          </td>
                          <td className="text-center small">
                            <span className={est.comments > 0 ? 'fw-bold text-dark' : 'text-muted'}>{est.comments}</span>
                          </td>
                          <td>{renderActions(est, false)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile: stacked cards so actions are always reachable */}
              <div className="d-md-none d-flex flex-column gap-2">
                {mine.map(est => {
                  const subtitle = subtitleOf(est);
                  return (
                    <div key={est.guid} className="border rounded-3 p-2 text-center">
                      <div className="fw-semibold">{est.estimateName || <span className="text-muted fst-italic">{t('estimates.untitled')}</span>}</div>
                      {subtitle && <div className="text-muted small">{subtitle}</div>}
                      <div className="text-muted small mt-1">{locationOf(est)} · {formatDate(est.createdAt)}</div>
                      <div className="text-muted small d-flex justify-content-center gap-3 mt-1 mb-2">
                        <span className="d-inline-flex align-items-center gap-1"><Eye size={14} /> {est.views}</span>
                        <span className="d-inline-flex align-items-center gap-1"><MessageSquare size={14} /> {est.comments}</span>
                      </div>
                      {renderActions(est, true)}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Claimable guest estimates */}
      <div className="card shadow-sm px-2 px-sm-0">
        <div className="card-body px-0 px-sm-3 py-3 py-sm-4">
          <div className="d-flex align-items-center justify-content-between mb-3 gap-2">
            <h5 className="fw-bold m-0">{t('estimates.claimable')}</h5>
            {claimable.length > 0 && (
              <button className="btn btn-primary btn-sm flex-shrink-0" onClick={handleClaimAll} disabled={claiming}>
                {claiming ? (
                  <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> {t('estimates.claiming')}</>
                ) : (
                  <>{t('estimates.claimAll', { count: claimable.length })}</>
                )}
              </button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-4">
              <Loader2 size={20} className="text-muted" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : claimable.length === 0 ? (
            <p className="text-muted small mb-0">{t('estimates.noClaimable')}</p>
          ) : (
            <ul className="list-group list-group-flush">
              {claimable.map(est => (
                <li key={est.guid} className="list-group-item d-flex align-items-center justify-content-between gap-2 px-0">
                  <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <FileText size={16} className="text-muted flex-shrink-0" />
                    <div style={{ minWidth: 0 }}>
                      <div className="fw-semibold small text-truncate">{est.estimateName || est.projectType || t('estimates.fallbackName')}</div>
                      <div className="text-muted text-truncate" style={{ fontSize: 12 }}>
                        {[est.city, est.region].filter(Boolean).join(', ')} · {new Date(est.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-link btn-sm flex-shrink-0" onClick={() => navigate(`/estimate/${est.guid}`)}>
                    {t('estimates.actions.view')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <AlertTriangle size={18} className="text-danger" /> {t('estimates.deleteTitle')}
                </h5>
                <button type="button" className="btn-close" onClick={() => setDeleteTarget(null)} disabled={deleting} aria-label={t('auth:close')} />
              </div>
              <div className="modal-body">
                <p className="mb-0 small">
                  {t('estimates.deleteBody', { name: deleteTarget.estimateName || t('estimates.untitled') })}
                </p>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                  {t('auth:cancel')}
                </button>
                <button type="button" className="btn btn-danger d-inline-flex align-items-center gap-1" onClick={handleConfirmDelete} disabled={deleting}>
                  {deleting
                    ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> {t('estimates.deleting')}</>
                    : <><Trash2 size={16} /> {t('estimates.actions.delete')}</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MyEstimates;
